import { ScheduleRow } from "entities/weather/model/types";
import { useEffect, useRef, useState } from "react";
import { updateButtons } from "./updateButtons";
import { useResize } from "app/lib/hooks/useResize";

const COL = 90;
const GAP = 8;
const VISIBLE = 10;
const OVERLAP = 2;
const STEP = (COL + GAP) * (VISIBLE - OVERLAP);

export const useWeatherHours = (day: ScheduleRow) => {
  const { isScreenMd, isScreenLg } = useResize();

  const scrollRef = useRef<HTMLDivElement>(null);
  const touchedRight = useRef<boolean>(false);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);

  const [showPrevButton, setShowPrevButton] = useState<boolean>(false);
  const [showNextButton, setShowNextButton] = useState<boolean>(true);

  const isMobile = !isScreenMd;
  const isTablet = !isScreenLg

  const visibleInfo = isMobile || day.hours.length < 23 ? day.parts : day.hours;

  const scrollByStep = (dir: 1 | -1) => {
    const weatherSection = scrollRef.current;
    if (!weatherSection) return;
    weatherSection.scrollBy({ left: dir * STEP, behavior: "smooth" });
  };

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;

    const el = scrollRef.current;
    if (!el) return;

    isDragging.current = true;
    dragStartX.current = e.pageX;
    dragScrollLeft.current = el.scrollLeft;

    el.style.scrollBehavior = "auto";
    el.style.cursor = "grabbing";
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;

    e.preventDefault();
    const el = scrollRef.current;
    if (!el) return;

    const dx = e.pageX - dragStartX.current;
    el.scrollLeft = dragScrollLeft.current - dx;
  };

  const stopDrag = () => {
    const el = scrollRef.current;
    isDragging.current = false;

    if (el) {
      el.style.cursor = "grab";
      el.style.scrollBehavior = "smooth";
    }
  };

  useEffect(() => {
    const weatherSection = scrollRef.current;
    let left = 0;

    if (!weatherSection) return;
    touchedRight.current = false;
    setShowPrevButton(false);
    setShowNextButton(true);
    weatherSection.scrollTo({ left: 0, behavior: "smooth" });

    const isToday = new Date().toISOString().slice(0, 10);

    if (day.dateIso === isToday) {
      const currentHour = `${String(new Date().getHours()).padStart(2, "0")}:00`;
      const idx = day.hours.findIndex((h) => h.label === currentHour);

      if (idx >= 0) {
        left = Math.max(0, idx * 90);
      }
    }

    weatherSection.scrollTo({ left: left, behavior: "smooth" });
  }, [day.dateIso, day.hours]);

  useEffect(() => {
    const weatherSection = scrollRef.current;

    if (!weatherSection) return;

    const onScroll = () =>
      updateButtons(
        weatherSection,
        setShowPrevButton,
        setShowNextButton,
        touchedRight
      );
    const onResize = () =>
      updateButtons(
        weatherSection,
        setShowPrevButton,
        setShowNextButton,
        touchedRight
      );

    updateButtons(
      weatherSection,
      setShowPrevButton,
      setShowNextButton,
      touchedRight
    );

    weatherSection.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onResize);

    return () => {
      weatherSection.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return {
    scrollRef,
    scrollByStep,
    showPrevButton,
    showNextButton,
    isMobile,
    isTablet,
    visibleInfo,
    onMouseDown,
    onMouseMove,
    stopDrag,
  };
};
